import { useState } from "react";
import { Eye, EyeOff, KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { useAuth } from "../../store/AuthContext";

// Set (or change) the account password. Email-code sign-in keeps working either way, so a forgotten password is fixed by
// signing in with the code and setting a new one here. `onDone(user)` fires after a successful save.
export const PasswordForm = ({ onDone, compact = false, testId = "password-form" }) => {
  const { user, signIn } = useAuth();
  const has = user && !user.passwordless;
  const [current, setCurrent] = useState("");
  const [pw, setPw] = useState("");
  const [again, setAgain] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const weak = pw.length > 0 && pw.length < 8;
  const mismatch = again.length > 0 && again !== pw;
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (pw.length < 8) return setErr("Use at least 8 characters");
    if (pw !== again) return setErr("The two passwords don't match");
    setBusy(true);
    try {
      const r = await api.setPassword(pw, current);
      signIn({ access_token: localStorage.getItem("xpattern.token"), user: r.user });
      localStorage.setItem("xpattern.loginMode", "password");  // this computer's sign-in form opens on the password field from now on
      toast.success(has ? "Password changed" : "Password set — next time, sign in with your email and password");
      setCurrent(""); setPw(""); setAgain("");
      onDone?.(r.user);
    } catch (ex) { setErr(apiError(ex)); } finally { setBusy(false); }
  };
  const Input = ({ value, set, label, tid, autoFocus }) => (
    <label className="block">
      <span className="mb-1 block text-[11px] font-medium uppercase tracking-wide text-[#9aa0a8]">{label}</span>
      <input data-testid={tid} type={show ? "text" : "password"} value={value} onChange={(e) => set(e.target.value)} autoFocus={autoFocus} autoComplete={tid.includes("current") ? "current-password" : "new-password"} className="field w-full" />
    </label>
  );
  return (
    <form onSubmit={submit} className={compact ? "space-y-2.5" : "space-y-3"} data-testid={testId}>
      {/* called as plain functions (not <Input/>) so React doesn't remount the inputs on every keystroke and steal focus */}
      {has && Input({ value: current, set: setCurrent, label: "Current password", tid: `${testId}-current` })}
      {Input({ value: pw, set: setPw, label: has ? "New password" : "Password", tid: `${testId}-new`, autoFocus: !has })}
      {Input({ value: again, set: setAgain, label: "Type it again", tid: `${testId}-again` })}
      <div className="flex items-center justify-between text-[11px]">
        <button type="button" data-testid={`${testId}-show`} onClick={() => setShow(!show)} className="flex items-center gap-1 text-[#9aa0a8] hover:text-[#e8e9ec]">{show ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />} {show ? "Hide" : "Show"} password</button>
        <span className={weak || mismatch ? "text-[#f5a524]" : "text-[#6b7079]"} data-testid={`${testId}-hint`}>{weak ? "At least 8 characters" : mismatch ? "Doesn't match yet" : "8+ characters · letters and numbers"}</span>
      </div>
      {err && <div className="rounded-md border border-[#e5484d]/40 bg-[#e5484d]/10 px-3 py-2 text-[12px] text-[#ff9ea3]" data-testid={`${testId}-error`}>{err}</div>}
      <button data-testid={`${testId}-submit`} disabled={busy || !pw || !again} className="btn-red flex w-full items-center justify-center gap-2 py-2.5">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <KeyRound className="h-4 w-4" />} {has ? "Change password" : "Set my password"}
      </button>
    </form>
  );
};
